import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import {
  Button,
  Field,
  Modal,
  Popover,
  Select,
  StateBlock,
  useToast,
} from '@w7/ui'
import { surveillanceClient } from '@/api/surveillanceClient'
import { useStaffMe } from '@/api/hooks/shared/useStaffMe'
import { canGenerateSurveillanceBetaGraph } from '@/app/staffAccess'
import { RunnerOddsChart } from '@/features/surveillance/RunnerOddsChart'
import {
  APPLY_GRAPH_VIEW_OPTIONS,
  GENERATE_GRAPH_OPTIONS,
  applyGraphViewTypeToRunners,
  defaultGraphViewType,
  type SurveillanceGraphViewType,
} from '@/features/surveillance/graphViewType'
import { formatSurveillanceWinnerLabel } from '@/features/surveillance/surveillanceLabels'
import type {
  SurveillanceGraph,
  SurveillanceGraphFilter,
} from '@/features/surveillance/types'
import { resolvePlGraphEventId } from './plViewGraph'
import '@/features/surveillance/surveillance.css'

function filterKeyOf(filter: SurveillanceGraphFilter | null): string {
  if (!filter) return ''
  return `${filter.eventType}|${filter.eventId}|${filter.marketId}|${filter.playerNames.join(',')}`
}

/**
 * Lab PLStatement `graph-modal` — archive View Graph from Game Detail.
 * Reuses Surveillance Beta chart tools (generate + desired view + Highstock).
 */
export function PlStatementGraphModal({
  open,
  filter,
  eventName = '',
  onClose,
}: {
  open: boolean
  filter: SurveillanceGraphFilter | null
  /** Settlement event name — used when `filter.eventId` is a display name. */
  eventName?: string
  onClose: () => void
}) {
  const toast = useToast()
  const me = useStaffMe()
  const canGenerate = canGenerateSurveillanceBetaGraph(me.data)
  const [graph, setGraph] = useState<SurveillanceGraph | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [viewGraphType, setViewGraphType] = useState<SurveillanceGraphViewType>(() =>
    defaultGraphViewType('archive'),
  )
  const [chartEpoch, setChartEpoch] = useState(0)
  const [generateOpen, setGenerateOpen] = useState(false)
  const generateAnchor = useRef<HTMLButtonElement>(null)
  const filterKey = filterKeyOf(filter)

  useEffect(() => {
    if (!open || !filter) {
      setGraph(null)
      setLoading(false)
      setLoadError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    setGraph(null)
    void (async () => {
      // Settlement may stamp eventId = event name; resolve vendor id for BQ.
      const eventId = await resolvePlGraphEventId({
        eventId: filter.eventId,
        eventName: eventName || filter.eventId,
        marketId: filter.marketId,
      })
      if (cancelled) return
      const res = await surveillanceClient.getGraph({ ...filter, eventId })
      if (cancelled) return
      setLoading(false)
      if (!res.ok) {
        setLoadError(res.message)
        toast.error(res.message)
        return
      }
      const nextView = defaultGraphViewType('archive')
      setViewGraphType(nextView)
      setGraph({
        ...res.data,
        h: applyGraphViewTypeToRunners(res.data.h, nextView),
      })
      setChartEpoch((n) => n + 1)
    })()
    return () => {
      cancelled = true
    }
  }, [open, filterKey]) // eslint-disable-line react-hooks/exhaustive-deps -- filterKey stamps filter identity

  const regenerate = useMutation({
    mutationFn: (graphType: SurveillanceGraphViewType) =>
      surveillanceClient.regenerateGraph(
        filter?.marketId ?? '',
        graphType,
        filter?.eventId,
      ),
    onSuccess: (res) => toast.success(res.message),
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Unable to generate graph'),
  })

  function changeGraphTypeView(next: SurveillanceGraphViewType) {
    setViewGraphType(next)
    setGraph((prev) => {
      if (!prev) return prev
      return { ...prev, h: applyGraphViewTypeToRunners(prev.h, next) }
    })
    setChartEpoch((n) => n + 1)
  }

  function handleClose() {
    setGenerateOpen(false)
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose} title="Graph" size="lg">
      {!filter ? (
        <StateBlock icon="info" title="Missing market">
          Event / market ids are required to load the archive graph.
        </StateBlock>
      ) : loading ? (
        <StateBlock icon="info" title="Loading …">
          Fetching archive surveillance graph for this market.
        </StateBlock>
      ) : loadError ? (
        <StateBlock icon="info" title="Unable to load graph">
          {loadError}
        </StateBlock>
      ) : graph && graph.h.length === 0 ? (
        <StateBlock icon="info" title="No data available">
          No runner / bet timeline for this settlement.
        </StateBlock>
      ) : graph && graph.h.length > 0 ? (
        <div className="cm-surv-stack">
          <div className="cm-surv-result-head">
            <div>
              <span className="cm-surv-label">Winner Name</span>
              <strong>{formatSurveillanceWinnerLabel(graph.m.c, graph.m.wn)}</strong>
            </div>
            <div>
              <span className="cm-surv-label">Start time (UTC)</span>
              <strong>
                {graph.m.mst
                  ? new Date(graph.m.mst).toLocaleString(undefined, {
                      timeZone: 'UTC',
                    })
                  : '—'}
              </strong>
            </div>
          </div>

          <div className="cm-surv-graph-tools">
            {canGenerate ? (
              <div className="cm-surv-generate-wrap">
                <Button
                  ref={generateAnchor}
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="cm-surv-btn cm-surv-generate-btn"
                  busy={regenerate.isPending}
                  disabled={!filter.marketId || regenerate.isPending}
                  iconAfter="chevron-down"
                  aria-haspopup="menu"
                  aria-expanded={generateOpen}
                  onClick={() => setGenerateOpen((v) => !v)}
                >
                  Generate Graph
                </Button>
                <Popover
                  open={generateOpen}
                  onClose={() => setGenerateOpen(false)}
                  anchorRef={generateAnchor}
                  label="Generate Graph"
                  width={200}
                >
                  <div className="cm-surv-generate-menu" role="menu">
                    {GENERATE_GRAPH_OPTIONS.archive.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        role="menuitem"
                        className="cm-surv-generate-item"
                        disabled={regenerate.isPending}
                        onClick={() => {
                          setGenerateOpen(false)
                          regenerate.mutate(opt.value)
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </Popover>
              </div>
            ) : null}
            <Field label="Select Desired Graph" className="cm-surv-field-graph-view">
              {({ id }) => (
                <Select
                  id={id}
                  value={viewGraphType}
                  options={APPLY_GRAPH_VIEW_OPTIONS.archive.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                  onChange={(e) =>
                    changeGraphTypeView(e.target.value as SurveillanceGraphViewType)
                  }
                />
              )}
            </Field>
          </div>

          <div className="cm-surv-charts" key={chartEpoch}>
            {graph.h.map((runner) => (
              <RunnerOddsChart key={runner.i} runner={runner} market={graph.m} />
            ))}
          </div>
        </div>
      ) : (
        <StateBlock icon="info" title="Unable to load graph">
          Try again from Game Detail, or open Surveillance Beta for this market.
        </StateBlock>
      )}
    </Modal>
  )
}
